import React, { useState, useEffect, useCallback } from 'react';
import { Spin } from 'antd';
import {
    FieldTimeOutlined,
    CheckCircleOutlined,
    SafetyCertificateOutlined,
    CoffeeOutlined,
    ThunderboltOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    ComposedChart,
    BarChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import moment from 'moment';
import { useReports } from '../context/ReportsContext';
import { getDailyActivityReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

// Brand palette
const C = {
    productive: '#00AAF1',
    idle: '#f59e0b',
    break: '#94a3b8',
    shift: '#003263',
    late: '#ef4444',
    green: '#10b981',
    indigo: '#6366f1',
    purple: '#a855f7',
};
const PIE_COLORS = [C.productive, C.idle, C.break, C.indigo, C.purple, C.green];
const STATUS_COLORS = { Present: C.green, Late: C.idle, Absent: C.late, 'Half Day': C.indigo };
const MODE_COLORS = { Office: C.productive, WFH: C.purple, Hybrid: C.indigo };

const card = 'bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs';
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

const FALLBACK_DAILY_ACTIVITY = {
    hasData: false,
    isFallback: true,
    summary: {
        totalShiftHours: '168.0h',
        totalProductiveHours: '142.5h',
        totalBreakHours: '13.1h',
        totalIdleHours: '12.4h',
        totalAttendanceOvertime: '8.5h',
        totalTaskOvertime: '6.0h',
        earlyShortfall: '1.2h',
        productiveRatio: 85,
        punctualityRate: 95,
        attendanceRecords: 38,
        uniqueEmployees: 8,
        lateCount: 2,
        earlyOutCount: 1,
    },
    timeComposition: [
        { name: 'Productive', value: 142.5 },
        { name: 'Idle (contextual)', value: 12.4 },
        { name: 'Break', value: 13.1 },
    ],
    statusBreakdown: [
        { name: 'Present', value: 33 },
        { name: 'Late', value: 2 },
        { name: 'Half Day', value: 2 },
        { name: 'Absent', value: 1 },
    ],
    workModeBreakdown: [
        { name: 'Office', value: 26 },
        { name: 'WFH', value: 8 },
        { name: 'Hybrid', value: 4 },
    ],
    overtimeComparison: [
        { name: 'Attendance Overtime', value: 8.5 },
        { name: 'Task Overtime', value: 6.0 },
    ],
    idleReasons: [
        { reason: 'Waiting for Code Review', hoursValue: 4.5, hours: '4.5h', minutes: 270, percentage: 36 },
        { reason: 'Client Specs Clarification', hoursValue: 3.5, hours: '3.5h', minutes: 210, percentage: 28 },
        { reason: 'CI/CD Pipeline Build Delay', hoursValue: 2.5, hours: '2.5h', minutes: 150, percentage: 20 },
        { reason: 'Cross-Team Sync & Planning', hoursValue: 1.9, hours: '1.9h', minutes: 114, percentage: 16 },
    ],
    dailyTrend: [
        { date: '2026-10-14', shiftHours: 24.0, productiveHours: 20.5, idleHours: 1.8, breakHours: 1.7, present: 6, late: 0 },
        { date: '2026-10-15', shiftHours: 28.0, productiveHours: 23.8, idleHours: 2.1, breakHours: 2.1, present: 7, late: 1 },
        { date: '2026-10-16', shiftHours: 24.0, productiveHours: 20.2, idleHours: 1.9, breakHours: 1.9, present: 6, late: 0 },
        { date: '2026-10-17', shiftHours: 28.0, productiveHours: 24.1, idleHours: 2.0, breakHours: 1.9, present: 7, late: 0 },
        { date: '2026-10-20', shiftHours: 24.0, productiveHours: 20.0, idleHours: 1.9, breakHours: 2.1, present: 6, late: 1 },
        { date: '2026-10-21', shiftHours: 20.0, productiveHours: 17.2, idleHours: 1.4, breakHours: 1.4, present: 5, late: 0 },
        { date: '2026-10-22', shiftHours: 20.0, productiveHours: 16.7, idleHours: 1.3, breakHours: 2.0, present: 5, late: 0 },
    ],
    employeeBreakdown: [
        { name: 'Muhammad Bilal', days: 5, late: 0, shiftHours: 40.0, productiveHours: 35.2, idleHours: 2.8, productiveRatio: 88 },
        { name: 'Muhammad Tahir', days: 5, late: 1, shiftHours: 38.0, productiveHours: 32.5, idleHours: 3.1, productiveRatio: 86 },
        { name: 'Sarah Chen', days: 5, late: 0, shiftHours: 36.0, productiveHours: 31.0, idleHours: 2.6, productiveRatio: 86 },
        { name: 'Priyah Patel', days: 4, late: 0, shiftHours: 32.0, productiveHours: 27.5, idleHours: 2.3, productiveRatio: 86 },
        { name: 'David Kim', days: 4, late: 1, shiftHours: 30.0, productiveHours: 25.0, idleHours: 2.5, productiveRatio: 83 },
    ],
};

function EmptyChart({ label = 'No records in the selected period' }) {
    return (
        <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
            <InboxOutlined style={{ fontSize: 22 }} />
            <span>{label}</span>
        </div>
    );
}

function ChartCard({ title, subtitle, children, className = '' }) {
    return (
        <div className={`${card} ${className}`}>
            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400 mb-4">{subtitle}</p>}
            {children}
        </div>
    );
}

function Kpi({ label, value, sub, icon, tone = 'text-slate-900 dark:text-white' }) {
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs">
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                <span>{label}</span>
                {icon}
            </div>
            <div className={`text-2xl sm:text-3xl font-black font-headline ${tone}`}>{value ?? '—'}</div>
            {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sub}</div>}
        </div>
    );
}

function Donut({ data, colorFor, centerLabel, centerValue }) {
    const nonZero = (data || []).filter(d => d.value > 0);
    if (nonZero.length === 0) return <EmptyChart />;
    return (
        <div className="relative h-56">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie data={nonZero} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="85%" paddingAngle={2} stroke="none">
                        {nonZero.map((d, i) => <Cell key={d.name} fill={colorFor(d, i)} />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                </PieChart>
            </ResponsiveContainer>
            {centerValue !== undefined && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xl font-black text-slate-900 dark:text-white font-headline">{centerValue}</span>
                    <span className="text-xs text-slate-400">{centerLabel}</span>
                </div>
            )}
        </div>
    );
}

function Legendish({ data, colorFor, suffix = '' }) {
    const total = (data || []).reduce((a, d) => a + (d.value || 0), 0);
    return (
        <div className="space-y-1.5 text-xs font-semibold mt-3">
            {(data || []).map((d, i) => (
                <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                    <span className="flex items-center gap-2 truncate">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorFor(d, i) }} />
                        <span className="truncate">{d.name}</span>
                    </span>
                    <span className="font-extrabold">
                        {total > 0 ? Math.round((d.value / total) * 100) : 0}% ({d.value}{suffix})
                    </span>
                </div>
            ))}
        </div>
    );
}

export default function DailyActivityTab() {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(() => getCachedTab('daily_activity'));

    const loadData = useCallback(async () => {
        const cached = getCachedTab('daily_activity');
        if (cached) {
            setData(cached);
            return;
        }
        setLoading(true);
        try {
            const res = await getDailyActivityReport({
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                teamMember: filters.teamMember,
                department: filters.department,
            });
            if (res && res.success && res.result) {
                setData(res.result);
                setCachedTab('daily_activity', res.result);
            }
        } catch (err) {
            console.error('Error fetching daily activity report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading && !data) {
        return (
            <div className={`${card} py-20 text-center`}>
                <Spin size="large" />
                <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    Aggregating attendance, shift &amp; idle activity...
                </p>
            </div>
        );
    }

    const isFallback = Boolean(data?.isFallback || (!data?.hasData && (!data?.summary?.attendanceRecords || data.summary.attendanceRecords === 0)));
    const activeData = isFallback ? FALLBACK_DAILY_ACTIVITY : (data || FALLBACK_DAILY_ACTIVITY);

    const s = activeData?.summary || {};
    const trend = (activeData?.dailyTrend || []).map(d => ({ ...d, label: moment(d.date).format('ddd D') }));
    const idleReasons = (activeData?.idleReasons || []).map(r => ({ name: r.reason, value: r.hoursValue }));
    const employees = (activeData?.employeeBreakdown || []).slice(0, 12);

    return (
        <div className="space-y-6">
            {/* Fallback notification pill */}
            {isFallback && (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs">
                    <div className="flex items-center gap-2 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                        <span>Simulated Demonstration Telemetry: No shift check-ins recorded for this exact filter window. Displaying benchmark activity distribution.</span>
                    </div>
                    <span className="font-mono text-[11px] uppercase tracking-wider font-bold opacity-80 shrink-0">Benchmark Fallback</span>
                </div>
            )}

            {/* Idle-time policy banner (PRD §6) */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-xs">
                        <SafetyCertificateOutlined />
                    </div>
                    <div>
                        <h3 className="text-sm sm:text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            Daily Activity &amp; Time Utilization
                        </h3>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            <strong>Idle Time rule:</strong> idle time is contextual and is never deducted from Productive Hours.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 px-2.5 py-1 rounded-lg bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {s.attendanceRecords ?? 0} records • {s.uniqueEmployees ?? 0} employees
                    </span>
                    <TabHelpButton tabKey="daily_activity" />
                </div>
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi
                    label="Productive Time"
                    value={s.totalProductiveHours}
                    sub={s.productiveRatio != null ? `${s.productiveRatio}% of ${s.totalShiftHours} shift` : 'No shift data'}
                    icon={<FieldTimeOutlined className="text-[#00AAF1]" />}
                    tone="text-[#00AAF1]"
                />
                <Kpi
                    label="Contextual Idle"
                    value={s.totalIdleHours}
                    sub="Logged, not deducted"
                    icon={<CoffeeOutlined className="text-amber-500" />}
                    tone="text-amber-500"
                />
                <Kpi
                    label="Task Overtime"
                    value={s.totalTaskOvertime}
                    sub={`Attendance OT: ${s.totalAttendanceOvertime ?? '0h'} (separate)`}
                    icon={<ThunderboltOutlined className="text-purple-500" />}
                    tone="text-purple-600 dark:text-purple-400"
                />
                <Kpi
                    label="Punctuality"
                    value={s.punctualityRate != null ? `${s.punctualityRate}%` : '—'}
                    sub={`${s.lateCount ?? 0} late • ${s.earlyOutCount ?? 0} early out`}
                    icon={<CheckCircleOutlined className="text-emerald-500" />}
                    tone="text-emerald-600 dark:text-emerald-400"
                />
            </div>

            {/* Row 1: Daily trend + time composition */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ChartCard
                    className="lg:col-span-2"
                    title="Shift vs Productive vs Idle — Daily Trend"
                    subtitle="Stacked productive / idle / break hours per day, with total shift hours as a line"
                >
                    <div className="h-72">
                        {trend.length === 0 ? <EmptyChart /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.25} vertical={false} />
                                    <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="h" />
                                    <Tooltip {...tooltipStyle} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="productiveHours" name="Productive" stackId="t" fill={C.productive} radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="idleHours" name="Idle" stackId="t" fill={C.idle} />
                                    <Bar dataKey="breakHours" name="Break" stackId="t" fill={C.break} radius={[6, 6, 0, 0]} />
                                    <Line type="monotone" dataKey="shiftHours" name="Shift" stroke={C.indigo} strokeWidth={2} dot={{ r: 3 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </ChartCard>

                <ChartCard title="Time Composition" subtitle="Where tracked shift time went">
                    <Donut
                        data={activeData?.timeComposition}
                        colorFor={(d, i) => [C.productive, C.idle, C.break][i]}
                        centerValue={s.productiveRatio != null ? `${s.productiveRatio}%` : '—'}
                        centerLabel="productive"
                    />
                    <Legendish data={activeData?.timeComposition} colorFor={(d, i) => [C.productive, C.idle, C.break][i]} suffix="h" />
                </ChartCard>
            </div>

            {/* Row 2: Idle reasons + attendance status + work mode */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ChartCard title="Idle Time by Reason" subtitle="Reasons employees selected when going idle">
                    <Donut data={idleReasons} colorFor={(d, i) => PIE_COLORS[i % PIE_COLORS.length]} />
                    {idleReasons.length > 0 && (
                        <Legendish data={idleReasons} colorFor={(d, i) => PIE_COLORS[i % PIE_COLORS.length]} suffix="h" />
                    )}
                </ChartCard>

                <ChartCard title="Attendance Status" subtitle="Present, late, half-day and absent records">
                    <Donut
                        data={activeData?.statusBreakdown}
                        colorFor={(d, i) => STATUS_COLORS[d.name] || PIE_COLORS[i % PIE_COLORS.length]}
                        centerValue={s.attendanceRecords ?? 0}
                        centerLabel="records"
                    />
                    <Legendish data={activeData?.statusBreakdown} colorFor={(d, i) => STATUS_COLORS[d.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                </ChartCard>

                <ChartCard title="Work Mode" subtitle="Office vs WFH vs Hybrid days">
                    <Donut data={activeData?.workModeBreakdown} colorFor={(d, i) => MODE_COLORS[d.name] || PIE_COLORS[i]} />
                    <Legendish data={activeData?.workModeBreakdown} colorFor={(d, i) => MODE_COLORS[d.name] || PIE_COLORS[i]} />
                </ChartCard>
            </div>

            {/* Row 3: Per-employee productive vs idle + overtime comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ChartCard
                    className="lg:col-span-2"
                    title="Employee Time Breakdown"
                    subtitle="Productive vs idle hours per employee (top 12 by productive hours). Not a ranking of performance."
                >
                    <div style={{ height: Math.max(240, employees.length * 34) }}>
                        {employees.length === 0 ? <EmptyChart /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={employees} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.25} horizontal={false} />
                                    <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} unit="h" />
                                    <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={120} />
                                    <Tooltip {...tooltipStyle} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="productiveHours" name="Productive" stackId="e" fill={C.productive} />
                                    <Bar dataKey="idleHours" name="Idle" stackId="e" fill={C.idle} radius={[0, 6, 6, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </ChartCard>

                <ChartCard title="Overtime: Attendance vs Task" subtitle="Kept separate per PRD — never summed">
                    <div className="h-56">
                        {(activeData?.overtimeComparison || []).every(d => !d.value) ? <EmptyChart label="No overtime recorded" /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={activeData?.overtimeComparison} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.25} vertical={false} />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="h" />
                                    <Tooltip {...tooltipStyle} />
                                    <Bar dataKey="value" name="Hours" radius={[8, 8, 0, 0]}>
                                        <Cell fill={C.indigo} />
                                        <Cell fill={C.purple} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                    <div className="text-xs text-slate-400 mt-3">
                        Early clock-out shortfall: <strong className="text-slate-700 dark:text-slate-200">{s.earlyShortfall ?? '0h'}</strong>
                    </div>
                </ChartCard>
            </div>

            {/* Row 4: Daily attendance headcount & lateness */}
            <ChartCard title="Daily Headcount &amp; Lateness" subtitle="Employees clocked in per day, with late arrivals">
                <div className="h-56">
                    {trend.length === 0 ? <EmptyChart /> : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.25} vertical={false} />
                                <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip {...tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="present" name="Clocked in" fill={C.green} radius={[6, 6, 0, 0]} />
                                <Bar dataKey="late" name="Late" fill={C.late} radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </ChartCard>
        </div>
    );
}
